import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TableComponent from './Tablecomponent';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Breadcrumbs, Button, Dialog, DialogActions, DialogContent, IconButton, Paper, Typography } from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { getDateAsPerFormat } from '../../_helpers/reusablefunctions/dateSetter';
import ApiService from '../../_services/ApiService';
import { setLoader, setSnackBar, setSnackBarMessage, setSnackBarVariant } from '../../redux';
import { formSnackbar } from '../../_services/_constants/snackbar.constant';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewRevenueCard from './ViewRevenueCard';
import SnackbarContainer from '../Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import useSplitStatus from './useSplitStatus';
import SplitProgress from './SplitProgress';
import { default_limit } from '../../_services/_constants/recordLimitset.constant';

// the split is queued on a worker now, so the button reports where that run is
const SPLIT_BUTTON_LABEL = {
    pending: 'QUEUED…',
    processing: 'SPLITTING…',
    success: 'ALREADY SPLIT',
    failed: 'RETRY SPLIT',
};
export default function ViewRevenue({ hideTotal }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    // the page holds 10 of these, the count in the footer is the API's total
    const [totalCount, setTotalCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(default_limit);
    // the split runs in the background, so the click opens a dialog that reports it
    const [splitDialogOpen, setSplitDialogOpen] = useState(false);
    const history = useHistory()
    // const rows = location?.state?.state?.data;
    const viewRevenue = JSON.parse(localStorage.getItem("revenueData"));

    const snackBarFunction = (message, variant, duration = 3000) => {
        dispatch(setSnackBar(true));
        dispatch(setSnackBarMessage(message));
        dispatch(setSnackBarVariant(variant));
        setTimeout(() => dispatch(setSnackBar(false)), duration);
    }

    const getExcelRows = () => {
        // this endpoint pages by index, not by row: offset 1 is the second page
        const offset = page;
        const limit = rowsPerPage;
        dispatch(setLoader(true))
        ApiService.get('getExcelData', { limit, offset }, viewRevenue.import_id).then(response => {
            const res = response.data
            if (response.status === 200) {
                dispatch(setLoader(false))
                setRows(res.data)
                setTotalCount(Number(res.total_count) || 0)
            }
            else {
                dispatch(setLoader(false))
            }
        }).catch(function (error) {
            dispatch(setLoader(false))
            return error.response;
        })
    }

    // the split runs on a celery worker: this checks where it stands, joins a run that
    // is already going, and drives the button off can_split rather than the split flag
    const split = useSplitStatus({
        importId: viewRevenue?.import_id,
        // check-split is paginated like getExcelData and answers nothing without these
        limit: rowsPerPage,
        offset: page,
        onSuccess: (data) => {
            snackBarFunction(
                `Split ${Number(data?.rows_shared || 0).toLocaleString()} rows into revenue shares.`,
                'success', 8000,
            )
            // the rows only carry their shares once the worker is done
            getExcelRows()
        },
        onFailed: (data) => {
            // an empty error_log is an operator making it re-runnable, not a crash
            if (data?.retryable) {
                snackBarFunction('This split was reset. You can run it again.', 'info', 8000)
            } else {
                snackBarFunction('The revenue split did not finish. Check the error log.', 'error', 8000)
            }
        },
        onError: ({ message }) => {
            snackBarFunction(message || formSnackbar.errors.errorMsg, 'error', 8000)
        },
    })

    useEffect(() => {
        getExcelRows()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage])

    const editRevenue = (data) => {
        history.push('/editrevenue', { state: { data: data } });
    }

    const columns = [
        {
            id: 'release_title', label: 'Release Title', headerName: '', flex: 1, template: (row) => {
                return (
                    <Box className="d-flex align-items-center gap-5"><UnfoldMoreIcon className='cursorPointer' onClick={() => editRevenue(row)} sx={{ transform: 'rotate(45deg)' }} />{row.release_title}</Box>
                );
            }
        },
        {
            id: 'duplicate', label: 'Duplicate Entry', headerName: '', flex: 1, template: (row) => {
                return (
                    <Box className="d-flex align-items-center gap-2 fw-bold text-danger">{row.duplicate ? "True" : 'False'}</Box>
                );
            }
        },
        { id: 'track_title', label: 'Track Title', field: "track_title", },
        {
            id: 'transaction_month', label: 'Transaction Month', headerName: '', flex: 1, template: (row) => {
                return (
                    <Box className="d-flex align-items-center gap-2">{getDateAsPerFormat(row.transaction_month, "DD Mon, YYYY")}</Box>
                );
            }
        },
        { id: 'label', label: 'Label', field: "label", },
        { id: 'artist', label: 'Artist', field: "artist", },
        { id: 'upc_code', label: 'UPC', field: "upc_code", },
        { id: 'isrc_code', label: 'ISRC', field: "isrc_code", },
        { id: 'release_catalog_id', label: 'Release Catalog ID', field: "release_catalog_id", },
        { id: 'track_catalog_id', label: 'Track Catalog ID', field: "track_catalog_id", },
        { id: 'format', label: 'Format', field: "format", },
        { id: 'parent_sale_id', label: 'Parent Sale Id', field: "parent_sale_id", },
        { id: 'transaction_id', label: 'Transaction ID', field: "transaction_id", },
        { id: 'account_id', label: 'Account ID', field: "account_id", },
        { id: 'contract_id', label: 'Contract ID', field: "contract_id", },
        { id: 'payee_id', label: 'Payee ID', field: "payee_id", },
        { id: 'platformName', label: 'Service', field: "platformName", },
        { id: 'channel_type', label: 'Channel', field: "channel_type", },
        { id: 'country_name', label: 'Territory', field: "country_name", },
        { id: 'count', label: 'quantity', field: "count", },
        { id: 'gross_revenue', label: 'Gross Revenue in INR', field: "gross_revenue", },
        { id: 'contract_rate', label: 'Contract Rate %', field: "contract_rate", },
        { id: 'net_revenue', label: 'Net Revenue in INR', field: "net_revenue", },
        { id: 'your_share', label: 'Your Share %', field: "your_share", },
        { id: 'opening_balance', label: 'Opening Balance in INR', field: "opening_balance", },
        { id: 'closing_balance', label: 'Closing Balance in INR', field: "closing_balance", },
    ];

    const backroute = () => {
        history.push('/importrevenue')
    }

    // re-running a split can never pay anyone twice -- each run credits only the
    // difference -- so the button is disabled for the user's sake, not for safety
    async function splitRevenue() {
        setSplitDialogOpen(true)
        const result = await split.start()
        if (result.alreadyDone) {
            snackBarFunction(result.message || 'This month has already been split.', 'info')
        } else if (result.started) {
            snackBarFunction('Revenue split started in the background.', 'success')
        }
    }

    return (
        <Box className="py-4 px-4">
            <Box className='d-flex align-items-center'>
                <Button className="icononly-btn d-flex align-items-center justify-content-start gap-3 text-body " onClick={backroute} style={{wordBreak:'break-word'}}><ArrowBackIcon />{viewRevenue.file}</Button>
                <Button
                    disabled={!split.canSplit || split.isStarting || split.isPolling}
                    onClick={() => (split.isStarting ? null : splitRevenue())}
                    variant="contained"
                    sx={{
                        width: '200px', backgroundColor: 'rgba(237, 50, 55, 1)', marginLeft: 'auto', gap: 1,
                        '&.Mui-disabled': { backgroundColor: 'rgba(237, 50, 55, 0.4)', color: '#fff' },
                    }}
                >
                    {SPLIT_BUTTON_LABEL[split.splitStatus] || 'SPLIT REVENUE'}
                    {(split.isStarting || split.isPolling) ? <CircularProgress sx={{ color: 'white', width: '20px !important', height: '20px !important' }} /> : null}
                </Button>
            </Box>
            {/* keeps a running split visible, and its skipped tracks after it finishes */}
            {split.isActive && (
                <Paper className="shadow-table" sx={{ p: 2, mt: 3, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <SplitProgress
                            starting={split.isStarting}
                            status={split.status}
                            splitStatus={split.splitStatus}
                            message={split.pollMessage}
                            error={split.pollError}
                        />
                    </Box>
                    {!split.isPolling && (split.pollMessage || split.pollError) && (
                        <IconButton size="small" onClick={split.dismiss} aria-label="dismiss split message">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Paper>
            )}
            {/* the click is answered immediately here and the dialog stays until it ends */}
            <Dialog
                open={splitDialogOpen}
                onClose={() => setSplitDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent sx={{ pt: 3 }}>
                    <SplitProgress
                        starting={split.isStarting}
                        status={split.status}
                        splitStatus={split.splitStatus}
                        message={split.pollMessage}
                        error={split.pollError}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSplitDialogOpen(false)}>
                        {/* closing never cancels the worker, the inline panel keeps reporting */}
                        {(split.isStarting || split.isPolling) ? 'Run in background' : 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>
            <ViewRevenueCard />
            {/* <BreadcrumbsList /> */}
            <Paper className="shadow-table" sx={{ width: '100%', mb: '3rem', mt: 4 }}>
                <TableComponent columns={columns} data={rows} totalCount={totalCount} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} />
            </Paper>
            <SnackbarContainer />
        </Box>
    );
}
